import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AICard, AIBadge } from "@/components/shared/AICard";
import { StatusPill } from "@/components/shared/StatusPill";
import { useFileSelectConfirm, FileSelectConfirmBar } from "@/components/shared/FileSelectConfirm";
import { api } from "@/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { UploadCloud, Loader2, FileText, ArrowRight, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useRole } from "@/api/hooks";

const TYPES = [
  "KYC Form", "Trade Licence", "VAT / TRN Certificate", "Aadhaar Card", "PAN Card", "CIN Certificate", "Authorised Signatory ID", "Credit Application",
  "Sales Enquiry / RFQ", "Vendor Quotation", "Customer Quotation", "Booking Confirmation",
  "Commercial Invoice", "Packing List", "House AWB", "Master AWB", "Bill of Lading", "MSDS",
  "Certificate of Origin", "Insurance Certificate", "Customs Declaration", "Delivery Order", "POD",
  "Vendor Invoice", "Customer Invoice", "Other Document",
];

export function AutoFileDialog({ open, onOpenChange, customerId }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const role = useRole();
  const [docType, setDocType] = useState("Commercial Invoice");
  const [result, setResult] = useState(null);

  const onFile = async (file) => {
    if (!file) return;
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("jobId", "auto");
    fd.append("docType", docType);
    if (customerId) fd.append("customerId", customerId);
    try {
      const { data } = await api.post("/documents/upload", fd);
      setResult(data);
      qc.invalidateQueries({ queryKey: ["docs"] });
      qc.invalidateQueries({ queryKey: ["exceptions"] });
      toast.success(data.escalated ? "Mismatch found — escalated to Exceptions" : data.aiRead ? "Document analyzed — human review required" : "Document uploaded — manual review required");
    } catch (err) {
      toast.error("Upload failed");
      throw err;
    }
  };
  const fileConfirm = useFileSelectConfirm(onFile);
  const busy = fileConfirm.busy;
  const reset = () => { setResult(null); fileConfirm.reset(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg" data-testid="autofile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><UploadCloud className="h-4 w-4 text-indigo-600" />Document intake</DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <AICard title="Assisted review">
              Upload a document for field extraction, Job routing and mismatch checks. Every result remains <strong>pending human approval</strong>; if assisted processing is unavailable, the document stays in manual review.
            </AICard>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Document type</label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="autofile-type" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <input ref={fileConfirm.inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={fileConfirm.onFileChosen} data-testid="autofile-input" />
            {!fileConfirm.pending ? (
              <button onClick={fileConfirm.openPicker} disabled={busy} data-testid="autofile-drop"
                className="w-full rounded-xl border-2 border-dashed border-slate-300 hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors p-8 flex flex-col items-center gap-2 disabled:opacity-70">
                <UploadCloud className="h-6 w-6 text-slate-400" /><span className="text-sm font-medium text-slate-600">Click to choose a PDF or scan</span><span className="text-[11px] text-slate-400">Routing assistance runs when available</span>
              </button>
            ) : (
              <FileSelectConfirmBar pending={fileConfirm.pending} busy={fileConfirm.busy} onSubmit={fileConfirm.submit} onCancel={fileConfirm.cancel} onReUpload={fileConfirm.reUpload} submitLabel="Submit document" />
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className={`rounded-xl border p-4 ${result.escalated ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
              <div className="flex items-center gap-2 mb-2">
                {result.escalated ? <AlertTriangle className="h-5 w-5 text-amber-500" /> : <FileText className="h-5 w-5 text-slate-500" />}
                <span className="font-display font-semibold text-slate-800">{result.escalated ? "Escalated — potential mismatch" : "Filed — awaiting review"}</span>
                {result.aiRead ? <AIBadge>{result.confidence} confidence</AIBadge> : <StatusPill status="Manual review" />}
              </div>
              <div className="text-[13px] text-slate-600">{result.name} → filed to <span className="font-mono-num font-semibold text-slate-800">{result.routedJobId || "—"}</span></div>
              {result.routingReason && <div className="text-[12px] text-slate-500 mt-0.5">{result.routingReason}</div>}
              {result.mismatch && <div className="mt-2 text-[12px] text-amber-800 bg-amber-100/60 rounded px-2 py-1">Mismatch: {result.mismatch}</div>}
            </div>
            {result.extracted && Object.keys(result.extracted).length > 0 && (
              <div className="rounded-xl border border-violet-200 bg-violet-50/70 p-3">
                <div className="mb-2"><AIBadge>Extracted</AIBadge></div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(result.extracted).slice(0, 6).map(([k, v]) => (
                    <div key={k}><div className="text-[10px] uppercase tracking-wider text-violet-500 font-semibold">{k}</div><div className="text-[13px] text-violet-950 font-mono-num truncate">{String(v)}</div></div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <Button variant="outline" onClick={reset} data-testid="autofile-another">File another</Button>
              {result.routedJobId && <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { onOpenChange(false); navigate(`/${["sales", "operations", "finance", "management"].includes(role) ? role : "operations"}/jobs/${result.routedJobId}`); }} data-testid="autofile-openjob">Open {result.routedJobId} <ArrowRight className="h-3.5 w-3.5 ml-1.5" /></Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
